import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate } from "react-router-dom";
import {
  FiCopy,
  FiDownload,
  FiEye,
  FiMoreVertical,
  FiPlus,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import {
  FaArrowLeft,
  FaArrowRight,
} from "react-icons/fa6";
import {
  LuCheckCircle,
  LuClock3,
  LuGraduationCap,
  LuHourglass,
  LuIndianRupee,
  LuShield,
  LuUser,
  LuUsers,
} from "react-icons/lu";
import PayoutDataTable from "./PayoutDataTable";
import PayoutEmptyState from "./PayoutEmptyState";
import PayoutDropdown from "./PayoutDropdown";
import PayoutFilterActions from "./PayoutFilterActions";
import PayoutFilterChips from "./PayoutFilterChips";
import PayoutKpiValue from "./PayoutKpiValue";
import PayoutSearchFilter from "./PayoutSearchFilter";
import PayoutTableCell from "./PayoutTableCell";
import { payoutApi } from "./api/payoutApiClient";
import {
  apiNumber,
  apiString,
  formatApiDateTime,
  transferRowFromApiRecord,
} from "./api/payoutApiAdapters";
import { usePayoutResource } from "./api/usePayoutResource";
import { writeSessionJson } from "./api/payoutMutationHelpers";
import { usePayoutAccess } from "./payoutAccessControl";

const formatINR = (amount: number, decimals = false) =>
  `\u20B9${amount.toLocaleString("en-IN", {
    minimumFractionDigits: decimals ? 2 : 0,
    maximumFractionDigits: decimals ? 2 : 0,
  })}`;

const displayText = (value: unknown) => {
  const text = String(value ?? "").trim();
  return text || "-";
};

const approvalVerifyPath = (
  action: "approve" | "reject",
  row: PendingApprovalRow,
) => {
  const params = new URLSearchParams({
    transferId: row.transferId,
    beneficiary: displayText(row.beneficiary),
    amount: formatINR(row.amount, true),
  });
  return `/payouts/transfers/pending-approval/${action}/verify?${params.toString()}`;
};

const apiRecordValue = (
  row: Record<string, unknown>,
  key: string,
): Record<string, unknown> => {
  const value = row[key];
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
};

const approvalStats = [
  {
    label: "Pending Transfers",
    value: "0",
    amount: 0,
    icon: <LuHourglass />,
    tone: "bg-[#F59E0B1A] text-[#F59E0B]",
  },
  {
    label: "My Approvals",
    value: "0",
    amount: 0,
    icon: <LuUser />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
  {
    label: "Finance Manager",
    value: "0",
    amount: 0,
    icon: <LuUsers />,
    tone: "bg-[#F59E0B1A] text-[#F97316]",
  },
  {
    label: "Principal Approval",
    value: "0",
    amount: 0,
    icon: <LuGraduationCap />,
    tone: "bg-[#6687FF1A] text-[#6687FF]",
  },
];

const transferCountFromSummary = (summary: Record<string, unknown>) =>
  apiNumber(
    summary,
    [
      "total_count",
      "totalCount",
      "total_transfers",
      "totalTransfers",
      "transfers_count",
      "transfersCount",
      "count",
    ],
    0,
  );

function SelectBox({
  label,
  value,
  options,
  onChange,
  className,
}: {
  label: string;
  value: string;
  options?: string[];
  onChange?: (value: string) => void;
  className?: string;
}) {
  return (
    <PayoutDropdown
      label={label}
      value={value}
      options={options}
      onChange={onChange}
      className={className}
    />
  );
}

function ApprovalChain({ chain }: { chain: string[] }) {
  const labels: Record<string, string> = {
    M: "Maker",
    C: "Checker",
    A: "Approver",
    U: "Auditor",
    MAKER: "Maker",
    CHECKER: "Checker",
    APPROVER: "Approver",
    AUDITOR: "Auditor",
    PS: "Maker",
    RM: "Maker",
    NK: "Maker",
    FM: "Finance",
    PR: "Principal",
  };

  if (!chain.length) {
    return <span className="font-semibold text-[#505E8E]">-</span>;
  }

  return (
    <div className="flex items-center gap-2">
      {chain.map((item, index) => (
        <React.Fragment key={`${item}-${index}`}>
          <div className="text-center">
            <span className="flex h-7 w-7 items-center justify-center rounded-full bg-[#6687FF1A] text-[10px] font-semibold text-[#6687FF]">
              {item}
            </span>
            <p className="mt-1 text-[9px] font-medium text-[#505E8E]">
              {labels[item] || item}
            </p>
          </div>
          {index < chain.length - 1 && (
            <span className="-mt-4 text-xs text-[#A3AED0]">{"->"}</span>
          )}
        </React.Fragment>
      ))}
    </div>
  );
}

type PendingApprovalRow = {
  transferId: string;
  beneficiary: string;
  detail: string;
  purpose: string;
  method?: string;
  account: string;
  accountDetail: string;
  fundingIfsc?: string;
  amount: number;
  requestedBy: string;
  makerUserId?: string;
  makerEmail?: string;
  role: string;
  requestedAt: string;
  chain: string[];
  priority?: string;
  beneficiaryBank?: string;
  beneficiaryIfsc?: string;
  beneficiaryAccountType?: string;
  beneficiaryEmail?: string;
  beneficiaryMobile?: string;
};

const emptyApprovalRow: PendingApprovalRow = {
  transferId: "",
  beneficiary: "",
  detail: "",
  purpose: "",
  method: "",
  account: "",
  accountDetail: "",
  fundingIfsc: "",
  amount: 0,
  requestedBy: "",
  makerUserId: "",
  makerEmail: "",
  role: "",
  requestedAt: "",
  chain: [],
  priority: "",
  beneficiaryBank: "",
  beneficiaryIfsc: "",
  beneficiaryAccountType: "",
  beneficiaryEmail: "",
  beneficiaryMobile: "",
};

const priorityFromAmount = (amount: number) =>
  amount > 150000 ? "Urgent" : amount > 100000 ? "High" : amount > 50000 ? "Medium" : "Low";

const approvalStageInitial = (stage: string) => {
  const normalized = stage.trim().toUpperCase();
  if (normalized === "MAKER" || normalized === "M") return "M";
  if (normalized === "CHECKER" || normalized === "C") return "C";
  if (normalized === "APPROVER" || normalized === "A") return "A";
  if (normalized === "AUDITOR" || normalized === "U") return "U";
  return normalized.slice(0, 2) || "";
};

const pendingApprovalFromApiRecord = (
  row: Record<string, unknown>,
  fallback: PendingApprovalRow,
): PendingApprovalRow => {
  const funding = apiRecordValue(row, "funding_account");
  const beneficiary = apiRecordValue(row, "beneficiary");
  const transfer = transferRowFromApiRecord(row, {
    transferId: fallback.transferId,
    referenceId: fallback.transferId,
    utrNumber: "",
    beneficiary: fallback.beneficiary,
    beneficiaryAccountNumber: fallback.detail,
    detail: fallback.detail,
    payoutAccount: fallback.account,
    payoutAccountDetail: fallback.accountDetail,
    purpose: fallback.purpose,
    category: "",
    method: "",
    amount: fallback.amount,
    status: "Pending",
    createdAt: fallback.requestedAt,
    settlement: "-",
  });
  const fundingName = apiString(
    funding,
    ["display_name", "displayName", "name", "account_name", "accountName"],
    "",
  );
  const fundingMasked =
    apiString(funding, ["account_number_masked", "accountNumberMasked"], "") ||
    apiString(funding, ["account_number_last4", "accountNumberLast4", "last4"], "");
  const fundingIfsc = apiString(funding, ["ifsc", "ifsc_code", "ifscCode"], "");
  const beneficiaryName = apiString(
    beneficiary,
    ["name", "beneficiary_name", "beneficiaryName", "name_entered", "nameEntered"],
    "",
  );
  const beneficiaryMasked =
    apiString(beneficiary, ["account_number_masked", "accountNumberMasked"], "") ||
    apiString(beneficiary, ["account_number_last4", "accountNumberLast4", "last4"], "");
  const beneficiaryIfsc = apiString(beneficiary, ["ifsc", "ifsc_code", "ifscCode"], "");
  const requestedBy =
    apiString(row, ["requested_by_name", "requestedByName", "maker_name", "makerName", "created_by_name", "createdByName"], "") ||
    apiString(row, ["maker_user_id", "makerUserId"], fallback.requestedBy) ||
    fallback.requestedBy;
  const makerUserId = apiString(
    row,
    ["maker_user_id", "makerUserId", "created_by", "createdBy", "created_by_id", "createdById"],
    fallback.makerUserId || "",
  );
  const makerEmail = apiString(
    row,
    ["maker_email", "makerEmail", "requested_by_email", "requestedByEmail", "created_by_email", "createdByEmail"],
    fallback.makerEmail || "",
  );
  const role =
    apiString(row, ["requested_by_role", "requestedByRole", "maker_role", "makerRole"], fallback.role) ||
    fallback.role;
  const rawPriority = apiString(row, ["priority"], "");
  const priorityNumber = apiNumber(row, ["priority"], 0);
  const chainValue =
    row.approval_chain ||
    row.approvalChain ||
    row.chain ||
    row.required_stages ||
    row.requiredStages;
  const chain = Array.isArray(chainValue)
    ? chainValue
        .map((item) =>
          approvalStageInitial(
            typeof item === "string"
              ? item
              : apiString(item as Record<string, unknown>, ["initials", "code", "role", "stage"], ""),
          ),
        )
        .filter(Boolean)
    : fallback.chain;
  const displayChain = chain.length && chain[0] !== "M" ? ["M", ...chain] : chain;
  const roleFromStage =
    apiString(row, ["current_stage", "currentStage"], "") ||
    (Array.isArray(chainValue) && chainValue.length
      ? String(chainValue[0] || "")
      : "");
  const requestedAt = formatApiDateTime(
    apiString(row, ["requested_at", "requestedAt", "created_at", "createdAt"], transfer.createdAt || fallback.requestedAt),
    fallback.requestedAt,
  );
  const accountDetail = [
    transfer.payoutAccountDetail ||
      (fundingMasked
        ? fundingMasked.startsWith("*")
          ? fundingMasked
          : `**** ${fundingMasked}`
        : ""),
    fundingIfsc ? `IFSC: ${fundingIfsc}` : "",
  ]
    .filter(Boolean)
    .join(" | ");
  const beneficiaryDetail = [
    transfer.detail ||
      (beneficiaryMasked
        ? beneficiaryMasked.startsWith("*")
          ? beneficiaryMasked
          : `**** ${beneficiaryMasked}`
        : ""),
    beneficiaryIfsc ? `IFSC: ${beneficiaryIfsc}` : "",
  ]
    .filter(Boolean)
    .join(" | ");

  return {
    ...fallback,
    transferId: transfer.transferId || fallback.transferId,
    beneficiary: displayText(transfer.beneficiary || beneficiaryName || apiString(row, ["beneficiary_id", "beneficiaryId"], fallback.beneficiary)),
    detail: displayText(beneficiaryDetail || fallback.detail),
    purpose: displayText(transfer.purpose || apiString(row, ["purpose", "narration", "client_reference_id", "clientReferenceId"], fallback.purpose)),
    method: transfer.method || fallback.method || "",
    account: displayText(transfer.payoutAccount || fundingName || apiString(row, ["funding_account_id", "fundingAccountId"], fallback.account)),
    accountDetail: displayText(accountDetail || fallback.accountDetail),
    fundingIfsc: transfer.fundingIfsc || fundingIfsc || fallback.fundingIfsc,
    amount: transfer.amount || fallback.amount,
    requestedBy: displayText(requestedBy),
    makerUserId,
    makerEmail,
    role: displayText(role || roleFromStage),
    requestedAt: displayText(requestedAt),
    chain: displayChain.length ? displayChain : fallback.chain,
    priority: rawPriority || (priorityNumber ? ["Low", "Medium", "High", "Urgent"][Math.min(3, Math.max(0, priorityNumber - 1))] : priorityFromAmount(transfer.amount || fallback.amount)),
    beneficiaryBank: transfer.beneficiaryBank || apiString(beneficiary, ["bank_name", "bankName", "bank"], fallback.beneficiaryBank || ""),
    beneficiaryIfsc: transfer.beneficiaryIfsc || beneficiaryIfsc || fallback.beneficiaryIfsc,
    beneficiaryAccountType: transfer.beneficiaryAccountType || apiString(beneficiary, ["account_type", "accountType"], fallback.beneficiaryAccountType || ""),
    beneficiaryEmail: transfer.beneficiaryEmail || apiString(beneficiary, ["email", "email_id", "emailId"], fallback.beneficiaryEmail || ""),
    beneficiaryMobile:
      transfer.beneficiaryMobile ||
      apiString(beneficiary, ["mobile", "mobile_number", "mobileNumber", "phone", "phone_number", "phoneNumber"], fallback.beneficiaryMobile || ""),
  };
};

function DetailMetric({
  label,
  value,
  helper,
  icon,
}: {
  label: string;
  value: string;
  helper?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="flex min-w-[190px] items-center gap-4 border-r border-[#E0E5F2] px-5 py-2 last:border-r-0">
      <span className="payout-kpi-icon flex h-10 w-10 shrink-0 items-center justify-center text-lg">
        {icon}
      </span>
      <span>
        <span className="block text-xs font-semibold text-[#505E8E]">
          {label}
        </span>
        <span className="mt-1 block text-sm font-semibold text-[#1B163B]">
          {value}
        </span>
        {helper && (
          <span className="mt-1 block text-xs font-medium text-[#505E8E]">
            {helper}
          </span>
        )}
      </span>
    </div>
  );
}

function DetailLine({
  label,
  value,
}: {
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="grid gap-4 border-b border-[#E0E5F2] py-3 text-sm md:grid-cols-[190px_minmax(0,1fr)]">
      <span className="font-medium text-[#505E8E]">{label}</span>
      <div className="font-semibold text-[#1B163B]">{value}</div>
    </div>
  );
}

function TimelineItem({
  label,
  time,
  done = false,
}: {
  label: string;
  time: string;
  done?: boolean;
}) {
  return (
    <div className="grid grid-cols-[28px_minmax(0,1fr)_170px] gap-3 text-sm">
      <div className="relative flex justify-center">
        <span
          className={
            "z-10 flex h-5 w-5 items-center justify-center rounded-full " +
            (done ? "bg-[#DCFCE7] text-[#16A34A]" : "bg-[#FEF3C7] text-[#B45309]")
          }
        >
          {done ? <LuCheckCircle className="text-xs" /> : <LuHourglass className="text-[10px]" />}
        </span>
        <span
          className={
            "absolute top-5 h-8 w-px " + (done ? "bg-[#16A34A]" : "bg-[#F59E0B]")
          }
        />
      </div>
      <span className="font-semibold text-[#1B163B]">{label}</span>
      <span className="text-right font-medium text-[#505E8E]">{time}</span>
    </div>
  );
}

function PendingApprovalDetailsDrawer({
  row,
  onClose,
  canApprove,
}: {
  row: PendingApprovalRow | null;
  onClose: () => void;
  canApprove: boolean;
}) {
  useEffect(() => {
    if (!row) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [row]);

  if (!row) return null;

  const pendingStatusClass = "bg-[#FEF3C7] text-[#B45309]";
  const copyReference = () => navigator.clipboard?.writeText(row.transferId);
  const downloadRequest = () => {
    const content = [
      "ClearN Pending Approval Transfer",
      `Transfer ID: ${row.transferId}`,
      `Beneficiary: ${row.beneficiary}`,
      `Amount: ${formatINR(row.amount, true)}`,
      `Status: Pending Approval`,
      `Requested At: ${row.requestedAt}`,
    ].join("\n");
    const blob = new Blob([content], { type: "text/plain;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${row.transferId}-pending-approval.txt`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex justify-end bg-[#1B163B]/20 backdrop-blur-[2px]">
      <button
        type="button"
        aria-label="Close pending approval details"
        className="absolute inset-0 cursor-default"
        onClick={onClose}
      />
      <aside className="payout-detail-drawer relative z-10 h-full w-full max-w-[1180px] overflow-y-auto bg-white px-8 py-7 shadow-2xl">
        <div className="mb-7 flex items-start justify-between gap-5">
          <div>
            <h2 className="text-[22px] font-semibold text-[#1B163B]">
              Transfer Details
            </h2>
            <div className="mt-4 flex items-center gap-2">
              <span className={`rounded-[6px] px-3 py-1 text-xs font-semibold ${pendingStatusClass}`}>
                Pending Approval
              </span>
              <span className="rounded-[6px] bg-[#A9B2CF1A] px-3 py-1 text-xs font-semibold text-[#505E8E]">
                Awaiting {row.chain.includes("PR") ? "Principal Approval" : "Finance Approval"}
              </span>
            </div>
            <p className="mt-3 text-xs font-semibold text-[#505E8E]">
              Reference ID
            </p>
            <div className="mt-1 flex items-center gap-2 text-lg font-semibold text-[#1B163B]">
              {displayText(row.transferId)}
              <button type="button" onClick={copyReference}>
                <FiCopy className="text-sm text-[#505E8E]" />
              </button>
            </div>
            <p className="mt-2 text-sm font-medium text-[#505E8E]">
              Requested on {row.requestedAt} {" - "} Pending approval
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <button
              type="button"
              onClick={downloadRequest}
              className="flex h-10 items-center gap-3 rounded-[6px] border border-[#BBD1FF] bg-white px-5 text-sm font-semibold text-[#2563EB]"
            >
              <FiDownload className="text-lg" />
              Download Request
            </button>
            {canApprove && (
              <>
                <Link
                  to={approvalVerifyPath("approve", row)}
                  className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                >
                  Approve
                </Link>
                <Link
                  to={approvalVerifyPath("reject", row)}
                  className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
                >
                  Reject
                </Link>
              </>
            )}
            <button
              type="button"
              onClick={onClose}
              className="text-2xl text-[#1B163B]"
            >
              <FiX />
            </button>
          </div>
        </div>

        <section className="mb-5 overflow-x-auto rounded-[8px] border border-[#E0E5F2] bg-white py-5 new-shadow">
          <div className="flex min-w-[980px] items-center justify-between">
            <DetailMetric
              label="Transfer Amount"
              value={formatINR(row.amount, true)}
              helper="Pending debit"
              icon={<LuShield />}
            />
            <DetailMetric
              label="Fees & Charges"
              value={formatINR(0)}
              helper="No charges"
              icon={<LuIndianRupee />}
            />
            <DetailMetric
              label="Net Amount"
              value={formatINR(row.amount, true)}
              icon={<FiDownload />}
            />
            <DetailMetric
              label="Status"
              value="Pending"
              icon={<LuHourglass />}
            />
            <DetailMetric
              label="Requested At"
              value={row.requestedAt}
              icon={<LuClock3 />}
            />
          </div>
        </section>

        <div className="grid gap-5 xl:grid-cols-2">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-4 text-lg font-semibold text-[#1B163B]">
              Transfer Information
            </h3>
            <DetailLine
              label="Source Account"
              value={
                <span>
                  {displayText(row.account)}
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    {displayText(row.accountDetail)}
                    {row.fundingIfsc ? ` | IFSC: ${row.fundingIfsc}` : ""}
                  </span>
                </span>
              }
            />
            <DetailLine label="Transfer Type" value={displayText(row.method || "Payout Transfer")} />
            <DetailLine label="Payment Mode" value={displayText(row.method)} />
            <DetailLine label="Requested At" value={row.requestedAt} />
            <DetailLine label="Purpose / Narration" value={row.purpose} />
            <DetailLine label="Priority" value={row.priority ?? "Medium"} />
            <DetailLine label="Channel" value="-" />
            <DetailLine
              label="Requested By"
              value={
                <span className="flex items-center gap-3">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#EDE9FE] text-xs font-semibold text-[#6D28D9]">
                    {row.requestedBy
                      .split(" ")
                      .map((part) => part[0])
                      .join("")
                      .slice(0, 2)}
                  </span>
                  <span>
                    {row.requestedBy}
                    <span className="block text-xs font-medium text-[#505E8E]">
                      {row.role}
                    </span>
                  </span>
                </span>
              }
            />
          </section>

          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-4 text-lg font-semibold text-[#1B163B]">
              Beneficiary Information
            </h3>
            <DetailLine label="Beneficiary Name" value={row.beneficiary} />
            <DetailLine label="Account Number" value={row.detail} />
            <DetailLine label="Bank Name" value={displayText(row.beneficiaryBank)} />
            <DetailLine label="IFSC Code" value={displayText(row.beneficiaryIfsc)} />
            <DetailLine label="Account Type" value={displayText(row.beneficiaryAccountType)} />
            <DetailLine label="Email" value={displayText(row.beneficiaryEmail)} />
            <DetailLine label="Phone" value={displayText(row.beneficiaryMobile)} />
          </section>
        </div>

        <div className="mt-5 grid gap-5 xl:grid-cols-[1fr_1fr_1.2fr]">
          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-5 text-lg font-semibold text-[#1B163B]">
              Amount Breakdown
            </h3>
            <div className="space-y-4 text-sm">
              <div className="flex justify-between">
                <span className="font-medium text-[#505E8E]">Transfer Amount</span>
                <span className="font-semibold text-[#1B163B]">{formatINR(row.amount, true)}</span>
              </div>
              <div className="flex justify-between border-b border-dashed border-[#A3AED0] pb-5">
                <span className="font-medium text-[#505E8E]">Fees & Charges</span>
                <span className="font-semibold text-[#1B163B]">{formatINR(0)}</span>
              </div>
              <div className="flex justify-between">
                <span className="font-semibold text-[#1B163B]">Net Amount</span>
                <span className="font-semibold text-[#1B163B]">{formatINR(row.amount, true)}</span>
              </div>
            </div>
          </section>

          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-5 text-lg font-semibold text-[#1B163B]">
              Approval Chain
            </h3>
            <ApprovalChain chain={row.chain} />
            <p className="mt-4 text-sm font-medium text-[#505E8E]">
              This request is waiting for the next approver in the configured workflow.
            </p>
          </section>

          <section className="row-span-2 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-5 text-lg font-semibold text-[#1B163B]">
              Timeline
            </h3>
            <div className="space-y-4">
              <TimelineItem label="Transfer Initiated" time={row.requestedAt} done />
              <TimelineItem label="Pending Approval" time={row.requestedAt} />
              <TimelineItem label="Checker Review" time="Awaiting action" />
              <TimelineItem label="Approver Review" time="Pending" />
              <TimelineItem label="Sent to Bank" time="-" />
              <TimelineItem label="Credited to Beneficiary" time="-" />
            </div>
          </section>

          <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
            <h3 className="mb-5 text-lg font-semibold text-[#1B163B]">
              Attachments (1)
            </h3>
            <button
              type="button"
              onClick={downloadRequest}
              className="flex w-full items-center justify-between rounded-[6px] border border-[#E0E5F2] px-4 py-3 text-left"
            >
              <span className="flex items-center gap-3">
                <span className="flex h-9 w-9 items-center justify-center rounded-[6px] bg-[#FEE2E2] text-xs font-semibold text-[#EF4444]">
                  PDF
                </span>
                <span className="text-sm font-semibold text-[#1B163B]">
                  Approval_Request.pdf
                  <span className="mt-1 block text-xs font-medium text-[#505E8E]">
                    237 KB
                  </span>
                </span>
              </span>
              <FiDownload className="text-[#505E8E]" />
            </button>
          </section>
        </div>

        <div className="mt-8 flex flex-wrap justify-end gap-3 border-t border-[#E0E5F2] pt-6">
          <button
            type="button"
            onClick={onClose}
            className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
          >
            Close
          </button>
          {canApprove && (
            <>
              <Link
                to={approvalVerifyPath("reject", row)}
                className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-7 text-sm font-semibold text-[#1B163B]"
              >
                Reject
              </Link>
              <Link
                to={approvalVerifyPath("approve", row)}
                className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-7 text-sm font-semibold text-white"
              >
                Approve
              </Link>
            </>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  );
}

function PayoutPendingApproval() {
  const navigate = useNavigate();
  const { canAccess } = usePayoutAccess();
  const canApproveTransfers = canAccess("transfers.approve");
  const canCreateTransfers = canAccess("transfers.create");
  const emptyApprovalSeed = useMemo<PendingApprovalRow[]>(() => [], []);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchField, setSearchField] = useState("Transfer ID");
  const [accountFilter, setAccountFilter] = useState("All Accounts");
  const [requestedByFilter, setRequestedByFilter] = useState("All Users");
  const [amountFilter, setAmountFilter] = useState("All Amounts");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [pageSize, setPageSize] = useState("10 per page");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedApprovalRow, setSelectedApprovalRow] =
    useState<PendingApprovalRow | null>(null);
  const [selectedApprovalIds, setSelectedApprovalIds] = useState<string[]>([]);
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    account: "All Accounts",
    requestedBy: "All Users",
    amount: "All Amounts",
    priority: "All",
    searchField: "Transfer ID",
  });
  const pendingSearchQuery = useMemo(() => ({ limit: 100 }), []);
  const approvalsResource = usePayoutResource<PendingApprovalRow[]>(
    emptyApprovalSeed,
    async (signal) => {
      const [approvals, users] = await Promise.all([
        payoutApi.pendingApprovals(pendingSearchQuery, signal),
        payoutApi.users({ limit: 100 }, signal).catch(() => []),
      ]);
      const userNameById = new Map(
        users
          .map((user) => {
            const id = apiString(user, ["user_id", "userId", "id"], "");
            const name =
              apiString(user, ["name", "full_name", "fullName"], "") ||
              apiString(user, ["email"], "");
            return id && name ? ([id, name] as const) : null;
          })
          .filter((entry): entry is readonly [string, string] => Boolean(entry)),
      );
      return Promise.all(
        approvals.map(async (approval) => {
          const paymentId = apiString(
            approval,
            ["payment_id", "paymentId", "transfer_id", "transferId", "id"],
            "",
          );
          const makerUserId = apiString(approval, ["maker_user_id", "makerUserId"], "");
          const approvalWithUser = makerUserId && userNameById.has(makerUserId)
            ? { ...approval, maker_name: userNameById.get(makerUserId) }
            : approval;
          if (!paymentId) {
            return pendingApprovalFromApiRecord(approvalWithUser, emptyApprovalRow);
          }
          try {
            const receipt = await payoutApi.paymentReceipt(paymentId, signal);
            return pendingApprovalFromApiRecord(
              { ...receipt, ...approvalWithUser, ...receipt },
              emptyApprovalRow,
            );
          } catch {
            try {
              const payment = await payoutApi.paymentDetails(paymentId, signal);
              return pendingApprovalFromApiRecord(
                { ...payment, ...approvalWithUser, ...payment },
                emptyApprovalRow,
              );
            } catch {
              return pendingApprovalFromApiRecord(approvalWithUser, emptyApprovalRow);
            }
          }
        }),
      );
    },
    [pendingSearchQuery],
  );
  const transferCountResource = usePayoutResource<number>(
    0,
    (signal) =>
      payoutApi
        .transfersSummary({ limit: 1000 }, signal)
        .then(transferCountFromSummary),
    [],
  );
  const baseApprovalRows = approvalsResource.data;
  const rowsPerPage = Number(pageSize.split(" ")[0]) || 10;
  const expandedApprovalRows = useMemo(
    () => baseApprovalRows,
    [baseApprovalRows],
  );
  const displayedApprovalStats = useMemo(() => {
    const totalAmount = baseApprovalRows.reduce((sum, row) => sum + row.amount, 0);
    const rowsWithChain = (code: string) =>
      baseApprovalRows.filter((row) => row.chain.includes(code));
    const sumRows = (rows: PendingApprovalRow[]) =>
      rows.reduce((sum, row) => sum + row.amount, 0);
    const financeRows = rowsWithChain("FM");
    const principalRows = rowsWithChain("PR");

    return approvalStats.map((stat) => {
      if (stat.label === "Pending Transfers") {
        return { ...stat, value: String(baseApprovalRows.length), amount: totalAmount };
      }
      if (stat.label === "My Approvals") {
        return { ...stat, value: String(baseApprovalRows.length), amount: totalAmount };
      }
      if (stat.label === "Finance Manager") {
        return { ...stat, value: String(financeRows.length), amount: sumRows(financeRows) };
      }
      if (stat.label === "Principal Approval") {
        return { ...stat, value: String(principalRows.length), amount: sumRows(principalRows) };
      }
      return { ...stat, value: "0", amount: 0 };
    });
  }, [baseApprovalRows]);
  const accountOptions = useMemo(
    () => [
      "All Accounts",
      ...Array.from(
        new Set(
          baseApprovalRows
            .map((row) => row.account)
            .filter((value) => value && value !== "-"),
        ),
      ),
    ],
    [baseApprovalRows],
  );
  const requestedByOptions = useMemo(
    () => [
      "All Users",
      ...Array.from(
        new Set(
          baseApprovalRows
            .map((row) => row.requestedBy)
            .filter((value) => value && value !== "-"),
        ),
      ),
    ],
    [baseApprovalRows],
  );
  const filteredApprovalRows = useMemo(() => {
    const query = appliedFilters.search.trim().toLowerCase();
    const getApprovalSearchValue = (
      row: (typeof expandedApprovalRows)[number],
      field: string,
    ) => {
      switch (field) {
        case "Beneficiary":
          return row.beneficiary;
        case "Purpose":
          return row.purpose;
        case "Account":
          return row.account;
        case "Requested By":
          return row.requestedBy;
        case "Priority":
          return row.priority;
        case "Transfer ID":
        default:
          return row.transferId;
      }
    };
    return expandedApprovalRows.filter((row) => {
      const matchesAccount =
        appliedFilters.account === "All Accounts" ||
        row.account === appliedFilters.account;
      const matchesRequestedBy =
        appliedFilters.requestedBy === "All Users" ||
        row.requestedBy === appliedFilters.requestedBy;
      const amountFilterText = appliedFilters.amount.toLowerCase();
      const matchesAmountByRange =
        appliedFilters.amount === "All Amounts" ||
        (amountFilterText.includes("below") && row.amount < 50000) ||
        (amountFilterText.includes("50,000") &&
          amountFilterText.includes("1,00,000") &&
          row.amount >= 50000 &&
          row.amount <= 100000) ||
        (amountFilterText.includes("above") && row.amount > 100000);
      const matchesAmount =
        appliedFilters.amount === "All Amounts" ||
        (appliedFilters.amount === "Below ₹50,000" && row.amount < 50000) ||
        (appliedFilters.amount === "₹50,000 - ₹1,00,000" &&
          row.amount >= 50000 &&
          row.amount <= 100000) ||
        (appliedFilters.amount === "Above ₹1,00,000" && row.amount > 100000);
      const matchesPriority =
        appliedFilters.priority === "All" || row.priority === appliedFilters.priority;
      const matchesSearch =
        !query ||
        String(getApprovalSearchValue(row, appliedFilters.searchField) || "")
          .toLowerCase()
          .includes(query);

      return (
        matchesAccount &&
        matchesRequestedBy &&
        (matchesAmount || matchesAmountByRange) &&
        matchesPriority &&
        matchesSearch
      );
    });
  }, [appliedFilters, expandedApprovalRows]);
  const pageCount = Math.max(1, Math.ceil(filteredApprovalRows.length / rowsPerPage));
  const visibleApprovalRows = filteredApprovalRows.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage,
  );
  const resultStart = filteredApprovalRows.length
    ? (currentPage - 1) * rowsPerPage + 1
    : 0;
  const resultEnd = Math.min(currentPage * rowsPerPage, filteredApprovalRows.length);
  const filteredApprovalIds = useMemo(
    () =>
      filteredApprovalRows
        .map((row) => row.transferId)
        .filter((id) => id && id !== "-"),
    [filteredApprovalRows],
  );
  const selectedApprovalIdSet = useMemo(
    () => new Set(selectedApprovalIds),
    [selectedApprovalIds],
  );
  const selectedApprovalRows = useMemo(
    () =>
      filteredApprovalRows.filter((row) =>
        selectedApprovalIdSet.has(row.transferId),
      ),
    [filteredApprovalRows, selectedApprovalIdSet],
  );
  const selectedApprovalTotal = useMemo(
    () => selectedApprovalRows.reduce((sum, row) => sum + row.amount, 0),
    [selectedApprovalRows],
  );
  const allFilteredApprovalsSelected =
    filteredApprovalIds.length > 0 &&
    filteredApprovalIds.every((id) => selectedApprovalIdSet.has(id));
  const toggleAllFilteredApprovals = () => {
    if (allFilteredApprovalsSelected) {
      setSelectedApprovalIds((current) =>
        current.filter((id) => !filteredApprovalIds.includes(id)),
      );
      return;
    }

    setSelectedApprovalIds((current) =>
      Array.from(new Set([...current, ...filteredApprovalIds])),
    );
  };
  const toggleApprovalSelection = (transferId: string) => {
    if (!transferId || transferId === "-") return;
    setSelectedApprovalIds((current) =>
      current.includes(transferId)
        ? current.filter((id) => id !== transferId)
        : [...current, transferId],
    );
  };
  const openBulkApprovalVerify = () => {
    if (selectedApprovalRows.length < 2) return;
    writeSessionJson("payoutBulkApprovalDraft", {
      approvals: selectedApprovalRows.map((row) => ({
        transferId: row.transferId,
        beneficiary: row.beneficiary,
        amount: row.amount,
        account: row.account,
        accountDetail: row.accountDetail,
        requestedBy: row.requestedBy,
        makerUserId: row.makerUserId,
        makerEmail: row.makerEmail,
      })),
      totalAmount: selectedApprovalTotal,
      count: selectedApprovalRows.length,
    });
    navigate("/payouts/transfers/pending-approval/bulk-approve/verify");
  };

  useEffect(() => {
    setAppliedFilters({
      search: searchQuery,
      account: accountFilter,
      requestedBy: requestedByFilter,
      amount: amountFilter,
      priority: priorityFilter,
      searchField,
    });
    setCurrentPage(1);
  }, [
    searchQuery,
    searchField,
    accountFilter,
    requestedByFilter,
    amountFilter,
    priorityFilter,
  ]);

  const clearFilters = () => {
    setSearchQuery("");
    setSearchField("Transfer ID");
    setAccountFilter("All Accounts");
    setRequestedByFilter("All Users");
    setAmountFilter("All Amounts");
    setPriorityFilter("All");
    setCurrentPage(1);
  };

  useEffect(() => {
    setSelectedApprovalIds((current) =>
      current.filter((id) => filteredApprovalIds.includes(id)),
    );
  }, [filteredApprovalIds]);

  const exportApprovals = () => {
    const headers = [
      "Transfer ID",
      "Beneficiary",
      "Purpose",
      "Account",
      "Amount",
      "Requested By",
      "Requested At",
      "Priority",
    ];
    const csvRows = filteredApprovalRows.map((row) =>
      [
        displayText(row.transferId),
        displayText(row.beneficiary),
        displayText(row.purpose),
        displayText(row.account),
        row.amount > 0 ? row.amount : "-",
        displayText(row.requestedBy),
        displayText(row.requestedAt),
        displayText(row.priority),
      ]
        .map((cell) => `"${String(cell).replace(/"/g, '""')}"`)
        .join(","),
    );
    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "payout-pending-approvals.csv";
    link.click();
    URL.revokeObjectURL(url);
  };
  const approvalTableRows = visibleApprovalRows.map((row) => [
    <div className="flex w-8 items-center justify-center">
      {canApproveTransfers && (
        <input
          type="checkbox"
          checked={selectedApprovalIdSet.has(row.transferId)}
          onChange={() => toggleApprovalSelection(row.transferId)}
          aria-label={`Select ${row.transferId}`}
          className="h-4 w-4 shrink-0 cursor-pointer accent-[#1B163B]"
        />
      )}
    </div>,
    <div className="flex w-[200px] items-center gap-2">
      <button
        type="button"
        aria-label="View details"
        onClick={() => setSelectedApprovalRow(row)}
        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[5px] text-[#1B163B] hover:bg-[#A9B2CF1A]"
      >
        <FiEye className="text-sm" />
      </button>
      {canApproveTransfers && (
        <>
          <Link
            to={approvalVerifyPath("approve", row)}
            className="flex h-8 min-w-[72px] items-center justify-center rounded-[5px] bg-[#1B163B] px-3 text-xs font-semibold text-white"
          >
            Approve
          </Link>
          <Link
            to={approvalVerifyPath("reject", row)}
            className="flex h-8 min-w-[64px] items-center justify-center rounded-[5px] border border-[#E0E5F2] bg-white px-3 text-xs font-semibold text-[#1B163B]"
          >
            Reject
          </Link>
        </>
      )}
    </div>,
    <PayoutTableCell
      value={displayText(row.transferId)}
      onClick={() => setSelectedApprovalRow(row)}
      valueClassName="font-semibold text-[#6687FF]"
    />,
    <span>
      <PayoutTableCell
        value={displayText(row.beneficiary)}
        valueClassName="font-medium text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate text-[#505E8E]" title={displayText(row.detail)}>
        {displayText(row.detail)}
      </span>
    </span>,
    <PayoutTableCell value={displayText(row.purpose)} />,
    <span>
      <PayoutTableCell
        value={displayText(row.account)}
        valueClassName="font-medium text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate text-[#505E8E]" title={displayText(row.accountDetail)}>
        {displayText(row.accountDetail)}
      </span>
    </span>,
    <span className="font-semibold">
      {row.amount > 0 ? formatINR(row.amount, true) : "-"}
    </span>,
    <span>
      <PayoutTableCell
        value={displayText(row.requestedBy)}
        valueClassName="font-medium text-[#1B163B]"
      />
      <span className="mt-1 block max-w-[220px] truncate text-[#505E8E]" title={displayText(row.role)}>
        {displayText(row.role)}
      </span>
    </span>,
    <PayoutTableCell value={displayText(row.requestedAt)} />,
    <ApprovalChain chain={row.chain} />,
  ]);

  return (
    <div className="w-full px-4 pt-4 pb-8">
      <div className="mb-7 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-[22px] font-semibold leading-7 text-[#1B163B]">
            Pending Approval
          </h1>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            Review and approve payout transfers
          </p>
        </div>
      </div>

      <div className="mb-8 flex flex-wrap items-center justify-between gap-5">
        <div className="grid flex-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          {displayedApprovalStats.map((stat) => (
            <div
              key={stat.label}
              className="flex min-h-[108px] items-center gap-4 rounded-[8px] border border-[#E0E5F2] bg-white px-4 py-5 new-shadow"
            >
              <div
                className={
                  "payout-kpi-icon flex h-10 w-10 shrink-0 items-center justify-center text-lg " +
                  stat.tone
                }
              >
                {stat.icon}
              </div>
              <div className="min-w-0">
                <p className="text-xs font-semibold text-[#505E8E]">
                  {stat.label}
                </p>
                <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
                  {stat.value}
                </p>
                <p className="mt-1 text-xs font-semibold text-[#1B163B]">
                  <PayoutKpiValue value={stat.amount} />
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <section className="payout-filter-card relative z-20 mb-5 rounded-[8px] border border-[#E0E5F2] bg-white new-shadow">
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-[#E0E5F2] px-5">
          <div className="flex items-center">
            <Link
              to="/payouts/transfers"
              className="relative flex h-14 items-center px-7 text-sm font-semibold text-[#1B163B]"
            >
              Track Transfers
              <span className="ml-2 rounded-full bg-[#6687FF1A] px-2 py-0.5 text-xs font-semibold text-[#6687FF]">
                {transferCountResource.data.toLocaleString("en-IN")}
              </span>
            </Link>
            <Link
              to="/payouts/transfers/pending-approval"
              className="relative flex h-14 items-center px-7 text-sm font-semibold text-[#6687FF]"
            >
              Pending Approval
              <span className="ml-2 rounded-full bg-[#FED7AA] px-2 py-0.5 text-xs font-semibold text-[#F97316]">
                {baseApprovalRows.length.toLocaleString("en-IN")}
              </span>
              <span className="absolute bottom-0 left-0 h-[2px] w-full bg-[#6687FF]" />
            </Link>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            {canApproveTransfers && selectedApprovalRows.length > 1 && (
              <button
                type="button"
                onClick={openBulkApprovalVerify}
                style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
                className="flex h-10 min-w-[132px] items-center justify-center rounded-[6px] px-5 text-sm font-semibold shadow-sm"
              >
                Bulk Approve
              </button>
            )}
            {canCreateTransfers && (
              <>
                <Link
                  to="/payouts/transfers/initiate"
                  style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
                  className="flex h-10 items-center gap-3 rounded-[6px] px-5 text-sm font-semibold shadow-sm"
                >
                  <FiPlus className="text-lg" />
                  Initiate Transfer
                </Link>
                <button
                  onClick={() => setBulkUploadOpen(true)}
                  className="flex h-10 items-center gap-3 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
                >
                  <FiUploadCloud className="text-lg text-[#6687FF]" />
                  Bulk Upload
                </button>
              </>
            )}
          </div>
        </div>

        <div className="payout-filter-grid payout-filter-grid--with-search payout-pending-approval-filter-grid px-5 py-5">
          <PayoutSearchFilter
            value={searchQuery}
            onChange={setSearchQuery}
            filterBy={searchField}
            onFilterByChange={setSearchField}
            filterOptions={[
              "Transfer ID",
              "Beneficiary",
              "Purpose",
              "Account",
              "Requested By",
              "Priority",
            ]}
            placeholder={`Search by ${searchField}`}
            className="w-full"
          />
          <>
            <SelectBox
              label="Account"
              value={accountFilter}
              options={accountOptions}
              onChange={setAccountFilter}
              className="w-full min-w-0"
            />
            <SelectBox
              label="Requested By"
              value={requestedByFilter}
              options={requestedByOptions}
              onChange={setRequestedByFilter}
              className="w-full min-w-0"
            />
            <SelectBox
              label="Amount"
              value={amountFilter}
              options={["All Amounts", "Below ₹50,000", "₹50,000 - ₹1,00,000", "Above ₹1,00,000"]}
              onChange={setAmountFilter}
              className="w-full min-w-0"
            />
            <SelectBox
              label="Priority"
              value={priorityFilter}
              options={["All", "Urgent", "High", "Medium", "Low"]}
              onChange={setPriorityFilter}
              className="w-full min-w-0"
            />
            <PayoutFilterActions
              onClear={clearFilters}
              onExport={exportApprovals}
              className="w-full lg:col-start-1"
            />
            <PayoutFilterChips
              className="col-span-full"
              onClearAll={clearFilters}
              chips={[
                {
                  label: "Account",
                  value: appliedFilters.account,
                  onRemove: () => setAccountFilter("All Accounts"),
                },
                {
                  label: "Requested By",
                  value: appliedFilters.requestedBy,
                  onRemove: () => setRequestedByFilter("All Users"),
                },
                {
                  label: "Amount",
                  value: appliedFilters.amount,
                  onRemove: () => setAmountFilter("All Amounts"),
                },
                {
                  label: "Priority",
                  value: appliedFilters.priority,
                  onRemove: () => setPriorityFilter("All"),
                },
                {
                  label: "Search",
                  value: appliedFilters.search,
                  onRemove: () => setSearchQuery(""),
                },
              ]}
            />
          </>
        </div>
      </section>

      <PayoutDataTable
        columns={[
          {
            key: "Select",
            label: (
              <span className="flex w-8 items-center justify-center">
                {canApproveTransfers && (
                  <input
                    type="checkbox"
                    checked={allFilteredApprovalsSelected}
                    onChange={toggleAllFilteredApprovals}
                    aria-label="Select all pending approvals"
                    className="h-4 w-4 cursor-pointer accent-[#1B163B]"
                  />
                )}
              </span>
            ),
          },
          "Actions",
          "Transfer ID",
          "Beneficiary",
          "Purpose",
          "Account",
          "Amount",
          "Requested By",
          "Requested At",
          "Approval Chain",
        ]}
        rows={approvalTableRows}
        minWidth="min-w-[1280px]"
        emptyState={
          <PayoutEmptyState
            icon={<LuHourglass />}
            title="No pending approvals"
            description="Transfers that need maker-checker approval will appear here after they match an approval workflow."
            action={
              canCreateTransfers ? (
                <Link
                  to="/payouts/transfers/initiate"
                  className="flex h-10 items-center rounded-[6px] bg-[#1B163B] px-5 text-sm font-semibold text-white"
                >
                  Initiate Transfer
                </Link>
              ) : null
            }
            secondaryAction={
              <Link
                to="/payouts/transfers"
                className="flex h-10 items-center rounded-[6px] border border-[#E0E5F2] bg-white px-5 text-sm font-semibold text-[#1B163B]"
              >
                View Transfers
              </Link>
            }
          />
        }
        footer={
          <div className="flex min-h-[64px] w-full items-center justify-between gap-4 bg-white px-5 py-4 text-xs font-medium text-[#505E8E]">
            <span className="min-w-max">
              Showing {resultStart} to {resultEnd} of {filteredApprovalRows.length} results
            </span>
            <div className="ml-auto flex min-w-max items-center gap-4">
              <PayoutDropdown
                value={pageSize}
                options={["10 per page", "25 per page", "50 per page"]}
                onChange={(value) => {
                  setPageSize(value);
                  setCurrentPage(1);
                }}
                className="min-w-[120px]"
              />
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowLeft className="text-xs" />
                </button>
                {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                  <button
                    type="button"
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === page
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {page}
                  </button>
                ))}
                {pageCount > 3 && (
                  <>
                    <span className="px-2 text-[#505E8E]">...</span>
                    <button
                      type="button"
                      onClick={() => setCurrentPage(pageCount)}
                      className={
                        "h-9 w-9 rounded-md " +
                        (currentPage === pageCount
                          ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                          : "border border-[#E0E5F2] text-[#1B163B]")
                      }
                    >
                      {pageCount}
                    </button>
                  </>
                )}
                <button
                  type="button"
                  disabled={currentPage === pageCount}
                  onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                  className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <FaArrowRight className="text-xs" />
                </button>
              </div>
            </div>
          </div>
        }
      />

      <section className="hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1160px] table-auto">
            <thead className="bg-[#A9B2CF1A] text-center">
              <tr className="text-left text-[11px] font-semibold text-[#505E8E]">
                <th className="px-4 py-3.5 text-center">Transfer ID</th>
                <th className="px-4 py-3.5 text-center">Beneficiary</th>
                <th className="px-4 py-3.5 text-center">Purpose</th>
                <th className="px-4 py-3.5 text-center">Account</th>
                <th className="px-4 py-3.5 text-center">Amount</th>
                <th className="px-4 py-3.5 text-center">Requested By</th>
                <th className="px-4 py-3.5 text-center">Requested At</th>
                <th className="px-4 py-3.5 text-center">Approval Chain</th>
                <th className="min-w-[220px] px-4 py-3.5 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {visibleApprovalRows.map((row) => (
                <tr
                  key={row.transferId}
                  className="border-t border-[#E0E5F2] text-xs font-medium text-[#1B163B] hover:bg-[#A9B2CF1A]"
                >
                  <td className="px-4 py-4 font-semibold text-[#6687FF]">
                    {row.transferId}
                  </td>
                  <td className="px-4 py-4">
                    <p>{row.beneficiary}</p>
                    <p className="mt-1 text-[#505E8E]">{row.detail}</p>
                  </td>
                  <td className="px-4 py-4">{row.purpose}</td>
                  <td className="px-4 py-4">
                    <p>{row.account}</p>
                    <p className="mt-1 text-[#505E8E]">{row.accountDetail}</p>
                  </td>
                  <td className="px-4 py-4 font-semibold">
                    {formatINR(row.amount, true)}
                  </td>
                  <td className="px-4 py-4">
                    <p>{row.requestedBy}</p>
                    <p className="mt-1 text-[#505E8E]">{row.role}</p>
                  </td>
                  <td className="px-4 py-4">{row.requestedAt}</td>
                  <td className="px-4 py-4">
                    <ApprovalChain chain={row.chain} />
                  </td>
                  <td className="min-w-[220px] px-4 py-4">
                    <div className="flex items-center justify-end gap-2">
                      {canApproveTransfers && (
                        <>
                          <Link
                            to={approvalVerifyPath("approve", row)}
                            className="flex h-8 items-center rounded-[5px] bg-[#1B163B] px-4 text-xs font-semibold text-white"
                          >
                            Approve
                          </Link>
                          <Link
                            to={approvalVerifyPath("reject", row)}
                            className="flex h-8 items-center rounded-[5px] border border-[#E0E5F2] bg-white px-4 text-xs font-semibold text-[#1B163B]"
                          >
                            Reject
                          </Link>
                        </>
                      )}
                      <button className="text-[#1B163B]">
                        <FiMoreVertical className="text-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex min-h-[64px] w-full items-center justify-between gap-4 border-t border-[#E0E5F2] px-5 py-4 text-xs font-medium text-[#505E8E]">
          <span className="min-w-max">
            Showing {resultStart} to {resultEnd} of {filteredApprovalRows.length} results
          </span>
          <div className="ml-auto flex min-w-max items-center gap-4">
            <PayoutDropdown
              value={pageSize}
              options={["10 per page", "25 per page", "50 per page"]}
              onChange={(value) => {
                setPageSize(value);
                setCurrentPage(1);
              }}
              className="min-w-[120px]"
            />
            <div className="flex items-center gap-2">
              <button
                type="button"
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowLeft className="text-xs" />
              </button>
              {[1, 2, 3].filter((page) => page <= pageCount).map((page) => (
                <button
                  type="button"
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={
                    "h-9 w-9 rounded-md " +
                    (currentPage === page
                      ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                      : "border border-[#E0E5F2] text-[#1B163B]")
                  }
                >
                  {page}
                </button>
              ))}
              {pageCount > 3 && (
                <>
                  <span className="px-2 text-[#505E8E]">...</span>
                  <button
                    type="button"
                    onClick={() => setCurrentPage(pageCount)}
                    className={
                      "h-9 w-9 rounded-md " +
                      (currentPage === pageCount
                        ? "border border-[#6687FF] bg-[#6687FF1A] text-[#6687FF]"
                        : "border border-[#E0E5F2] text-[#1B163B]")
                    }
                  >
                    {pageCount}
                  </button>
                </>
              )}
              <button
                type="button"
                disabled={currentPage === pageCount}
                onClick={() => setCurrentPage((page) => Math.min(pageCount, page + 1))}
                className="flex h-9 w-9 items-center justify-center rounded-md border border-[#E0E5F2] text-[#505E8E] disabled:cursor-not-allowed disabled:opacity-50"
              >
                <FaArrowRight className="text-xs" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {bulkUploadOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 px-4">
          <div className="w-full max-w-[560px] rounded-[8px] bg-white p-6 new-shadow">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-[20px] font-semibold text-[#1B163B]">
                  Bulk Upload Transfers
                </h2>
                <p className="mt-1 text-sm font-medium text-[#505E8E]">
                  Upload a transfer file to create multiple payouts.
                </p>
              </div>
              <button
                onClick={() => setBulkUploadOpen(false)}
                className="text-[#505E8E]"
              >
                <FiX className="text-xl" />
              </button>
            </div>
            <div className="mt-6 rounded-[8px] border border-dashed border-[#A3AED0] bg-[#F8FAFF] px-6 py-8 text-center">
              <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-[8px] bg-[#6687FF1A] text-[#6687FF]">
                <FiUploadCloud className="text-2xl" />
              </div>
              <p className="mt-4 text-sm font-semibold text-[#1B163B]">
                Drag and drop CSV/XLSX file here or click to upload
              </p>
              <p className="mt-1 text-xs font-medium text-[#505E8E]">
                Supported formats: CSV, XLSX. Maximum file size 5MB.
              </p>
            </div>
            <div className="mt-5 rounded-[6px] bg-[#6687FF1A] px-4 py-3 text-sm font-medium text-[#6687FF]">
              Use the approved payout template to avoid validation errors.
            </div>
            <div className="mt-6 flex items-center justify-end gap-4">
              <button
                onClick={() => setBulkUploadOpen(false)}
                className="h-10 rounded-[6px] border border-[#E0E5F2] bg-white px-6 text-sm font-semibold text-[#1B163B]"
              >
                Cancel
              </button>
              <button className="h-10 rounded-[6px] bg-edviron_black px-6 text-sm font-semibold text-white">
                Upload File
              </button>
            </div>
          </div>
        </div>
      )}

      <PendingApprovalDetailsDrawer
        row={selectedApprovalRow}
        onClose={() => setSelectedApprovalRow(null)}
        canApprove={canApproveTransfers}
      />
    </div>
  );
}

export default PayoutPendingApproval;
